import { embedSearchWidgetIsomorphicApiFetchWidget } from '#/modules/embedSearchWidget/api/embedSearchWidgetApiFetchWidget';
import { EmbedSearchWidgetContext } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import { embedSearchWidgetRouteGroup } from '#/modules/embedSearchWidget/routing/embedSearchWidget.routes';
import type { HorizonAppRouteGroupLoaderArgs } from '#/routing/classes/types/AppRouteGroupLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';

import { EmbedSearchWidgetLayoutLoadable } from './EmbedSearchWidgetLayoutLoadable';
import { data, type HeadersArgs, redirect } from 'react-router';
import { embedSearchWidgetSearchVolopsRoute } from '#/modules/embedSearchWidget/volops/routing/embedSearchWidgetVolops.routes';
import { embedSearchWidgetSearchJobsRoute } from '#/modules/embedSearchWidget/jobs/routing/embedSearchWidgetJobs.routes';

export const id = 'embed-search-widget-layout'; // only used on hypernova

export async function loader({
  request,
  context,
  params: { lang, embedSearchWidgetId },
}: HorizonAppRouteGroupLoaderArgs<typeof embedSearchWidgetRouteGroup>) {
  const [embedSearchWidget] = await Promise.all([
    embedSearchWidgetIsomorphicApiFetchWidget({ request, context })(
      embedSearchWidgetId,
    ),
    EmbedSearchWidgetLayoutLoadable.load(),
  ]);

  // We rely on the layout to redirect because if we add a new route to do so we'll
  // trigger 2 API requests to fetch the widget data instead of 1
  //
  // While this is not a huge problem, it does incur extra loading time for the user
  const url = new URL(request.url);
  const isRootRedirectRoute =
    url.pathname === `/${lang}/embed-search-widget/${embedSearchWidgetId}`;

  if (isRootRedirectRoute) {
    switch (embedSearchWidget.listingType) {
      case 'VOLOP':
        return redirect(
          embedSearchWidgetSearchVolopsRoute.with({
            lang,
            embedSearchWidgetId,
          }),
        );
      case 'JOB':
        return redirect(
          embedSearchWidgetSearchJobsRoute.with({ lang, embedSearchWidgetId }),
        );
    }
  }

  return data(
    { embedSearchWidget },
    {
      headers: {
        'X-Widget-Origins': embedSearchWidget.origins.join(' '),
      },
    },
  );
}

export function headers({ parentHeaders, loaderHeaders }: HeadersArgs) {
  parentHeaders.set('X-Widget-Origins', loaderHeaders.get('X-Widget-Origins')!);
  return parentHeaders;
}

export default function EmbedSearchWidgetLayoutHorizonRoute({
  loaderData: { embedSearchWidget },
}: HorizonAppRouteComponentProps<typeof loader>) {
  return (
    <EmbedSearchWidgetContext value={embedSearchWidget}>
      <EmbedSearchWidgetLayoutLoadable embedSearchWidget={embedSearchWidget} />
    </EmbedSearchWidgetContext>
  );
}
